import React, {useRef, useState} from 'react';
import {isBoolean} from 'lodash';
import ReactDOM from 'react-dom';
import FormActions from './FormActions';
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Button,
	ChakraProvider,
	Text,
} from 'components/ui/chakra';

export type ConfirmProps = {
	title: string;
	size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'xs' | '3xl' | '4xl' | '5xl' | '6xl' | 'full';
	content?: React.ReactNode | React.ReactNode[];
	validate?: () => true | string;
	onOk: () => void;
	onDestroy?: () => void;
};

const Modal: React.FC<ConfirmProps> = ({
	title,
	size = 'sm',
	content = '',
	validate = () => true,
	onOk,
	onDestroy,
}) => {
	const [error, setError] = useState('');
	const [isOpen, setIsOpen] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const cancelRef = useRef(null);

	const closeModal = () => {
		setIsOpen(false);
		if (onDestroy) setTimeout(onDestroy, 250);
	};

	const confirmModal = async () => {
		const validation = validate();

		if (isBoolean(validation)) {
			setError('');
			setIsSubmitting(true);
			try {
				await onOk();
				closeModal();
			} finally {
				setIsSubmitting(false);
			}
		} else {
			setError(String(validation));
		}
	};

	return (
		<>
			<AlertDialog
				size={size}
				isOpen={isOpen}
				leastDestructiveRef={cancelRef}
				onClose={closeModal}
			>
				<AlertDialogOverlay />
				<AlertDialogContent>
					<AlertDialogHeader fontSize='lg' fontWeight='bold'>
						{title}
					</AlertDialogHeader>

					<AlertDialogBody>
						{content}
						{error && (
							<Text color='red.500' fontSize='sm' mt={2}>
								{error}
							</Text>
						)}
					</AlertDialogBody>

					<AlertDialogFooter>
						<FormActions mt={0} pt={0} w='full'>
							<Button ref={cancelRef} onClick={closeModal} intent='secondary'>
								Cancel
							</Button>
							<Button intent='danger' isLoading={isSubmitting} onClick={confirmModal}>
								OK
							</Button>
						</FormActions>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
};

export const confirm = (props: ConfirmProps) => {
	const domNode = document.getElementById('confirm-modal-div') || document.createElement('div');

	if (domNode.id !== 'confirm-modal-div') {
		domNode.id = 'confirm-modal-div';
		document.body.appendChild(domNode);
	}

	const onDestroy = () => {
		ReactDOM.unmountComponentAtNode(domNode);
	};

	// eslint-disable-next-line react/no-render-return-value
	return ReactDOM.render(
		<ChakraProvider>
			<Modal {...props} onDestroy={onDestroy} />
		</ChakraProvider>,
		domNode,
	);
};
